// btc15/glossary.js — what every number on the BTC 15 tab means.
//
// The user's ask, 2026-09-16: an information icon, an explanation of every
// figure on every card, short snippets, and the full detail of how an agent
// actually reaches a decision.
//
// One source of truth, shared by the phone tab (diamond/Btc15Panel.js) and
// the desktop tab (Btc15Tab.js), so the two can never drift into explaining
// the same number differently.
//
// Every entry carries:
//   short   one line, used inline and as the snippet under the ⓘ
//   detail  the full account, in plain words
//   formula the arithmetic, exactly as the desk computes it (optional)
//   worked  a function of the LIVE context returning a worked example with
//           the numbers currently on screen, so the explanation is about
//           this window rather than a hypothetical one (optional)
//   seeAlso ids of related entries, rendered as tappable links
//
// Accuracy is the whole point of this file. Every formula here mirrors
// backend/btc15_desk.py; if one changes, change it in both places or the
// tab starts lying to the user with great confidence.

const c = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? null : `${(Number(v) * 100).toFixed(d)}¢`);
const p = (v) => (v == null || Number.isNaN(Number(v)) ? null : `${Math.round(Number(v) * 100)}%`);
const d2 = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v).toFixed(2));
const usd = (v) => (v == null || Number.isNaN(Number(v)) ? null : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fee1 = (price, contracts = 1) => (price == null ? null : 0.07 * price * (1 - price) * contracts);

export const GROUPS = [
  ["window", "The window"],
  ["market", "The market and what a contract costs"],
  ["agents", "The agents and their probabilities"],
  ["decision", "How a decision is actually made"],
  ["exits", "Getting out"],
  ["record", "The record, and how it is judged"],
  ["money", "Money, banks and ZBucks"],
];

export const ENTRIES = [
  // ── the window ──────────────────────────────────────────────────────────
  {
    id: "target", group: "window", term: "Target",
    short: "The price Bitcoin must finish at or above for “Up” to win.",
    detail:
      "Kalshi opens a new market every 15 minutes and asks one question: will Bitcoin settle at or above this number? " +
      "The target is not the price at the moment the window opens — it is a 60-second average of Bitcoin taken just " +
      "BEFORE the open. That detail matters more than it looks: it means at minute 0 the live price and the target are " +
      "the same number by construction, so there is no gap to read yet. Across 25 measured windows the gap at minute 0 " +
      "had a median of 0.13σ and never once exceeded 0.41σ, which is why the gap rule waits until minute 5.",
    seeAlso: ["now", "gap", "settlement", "z"],
  },
  {
    id: "now", group: "window", term: "Now",
    short: "The live Bitcoin price from Coinbase, arriving many times a second.",
    detail:
      "Straight off Coinbase's public price feed. The tab does not poll for this — it subscribes, and repaints in the " +
      "first animation frame after a new price exists, which is why the cents tick continuously while the dollars sit " +
      "still. The little pulsing dot means the feed is connected; the number beside it is prints per second.\n\n" +
      "This is the only number on the tab that is not the desk's opinion of anything. Everything else is derived.",
    worked: (x) => (x?.tps ? `Right now the feed is delivering about ${x.tps} price updates a second.` : null),
    seeAlso: ["target", "gap", "tps"],
  },
  {
    id: "gap", group: "window", term: "Gap (Now − Target)",
    short: "How far above or below the target Bitcoin is sitting, in dollars.",
    detail:
      "The arrow and the dollar figure next to “Now”. Green means Bitcoin is above the target, so “Up” is currently " +
      "winning; red means below. On its own the dollar gap says very little, because what matters is how big it is " +
      "relative to how much Bitcoin can still move before the bell — a $40 gap with ten minutes left is a coin flip, " +
      "and the same $40 with thirty seconds left is nearly settled. The number that carries that context is z.",
    worked: (x) => (x?.btc != null && x?.target != null
      ? `Now ${usd(x.btc)} − target ${usd(x.target)} = ${x.btc >= x.target ? "+" : "−"}${usd(Math.abs(x.btc - x.target))}.`
      : null),
    seeAlso: ["z", "target", "sigma"],
  },
  {
    id: "tps", group: "window", term: "Prints per second",
    short: "How many live Bitcoin prices arrived in the last second.",
    detail:
      "A health readout for the tape, not a market figure. The tab republishes on requestAnimationFrame, so this is " +
      "roughly how fast Coinbase is trading, capped by your screen's refresh rate. If it drops to zero the dot stops " +
      "and every derived number on screen is frozen at its last good value.",
    seeAlso: ["now"],
  },
  {
    id: "knownsofar", group: "window", term: "Already decided",
    short: "The share of past windows whose final direction was already fixed by this minute.",
    detail:
      "From the 2,999-window study behind this desk. At minute 0 it is 50% — a coin flip, by definition. By minute 10 " +
      "it is 82%, and by minute 13 it is 91%: in nine windows out of ten, whichever side is ahead at minute 13 is the " +
      "side that wins.\n\n" +
      "It is shown because it explains why late prices are so lopsided, and it is deliberately NEVER traded on. " +
      "Knowing that a window is 91% decided tells you nothing you can profit from, because the market knows it too and " +
      "charges 91¢ for it.",
    seeAlso: ["settlement", "echo-rule"],
  },
  {
    id: "settlement", group: "window", term: "Settlement",
    short: "A 60-second average of Bitcoin at the bell decides it — not the last tick.",
    detail:
      "At the close Kalshi takes a 60-second average of Bitcoin and compares it to the target. At or above, “Up” pays; " +
      "below, “Down” pays. Because it is an average of the final minute rather than a single print, the last minute is " +
      "largely decided before it starts, which is the real reason the “already decided” curve reaches 91% by minute 13.\n\n" +
      "A winning contract pays exactly $1. A losing one pays $0. There is nothing in between at settlement — though you " +
      "can sell before the bell for whatever the book will pay.",
    seeAlso: ["knownsofar", "mark", "payout"],
  },

  // ── the market ──────────────────────────────────────────────────────────
  {
    id: "price", group: "market", term: "Up / Down price",
    short: "The price of one contract in cents, which is also the market's probability.",
    detail:
      "On a prediction market these are the same number wearing two hats. A contract that pays $1 if Up happens, " +
      "trading at 75¢, means the market thinks Up is 75% likely — otherwise there would be free money on one side.\n\n" +
      "“Up” is quoted at the ASK, because that is what you would pay to buy it right now. “Down” is quoted at 1 minus " +
      "the BID, because buying Down is economically the same as selling Up, and you sell into the bid. That is why the " +
      "two numbers add to slightly more than 100%: the difference is the spread, and it is a cost, not an arbitrage.",
    formula: "Up = yes ask · Down = 1 − yes bid",
    worked: (x) => (x?.yesAsk != null && x?.yesBid != null
      ? `Up ${c(x.yesAsk)} and Down ${c(1 - x.yesBid)} sum to ${c(x.yesAsk + (1 - x.yesBid))} — the ${c(x.yesAsk - x.yesBid)} over 100¢ is the spread.`
      : null),
    seeAlso: ["mid", "spread", "fee", "contracts"],
  },
  {
    id: "mid", group: "market", term: "Kalshi mid",
    short: "Halfway between the best bid and the best ask — the market's fairest single number.",
    detail:
      "Neither side of the book is “the” price: one is what you pay to buy, the other what you get to sell. The mid " +
      "sits between them and is the cleanest estimate of what the market actually believes.\n\n" +
      "This is the number every agent is measured against, and the one they are corrected toward. It has beaten every " +
      "signal this desk has ever tested, which is a statement about how hard this market is, not about the agents.",
    formula: "mid = (yes bid + yes ask) ÷ 2",
    worked: (x) => (x?.yesBid != null && x?.yesAsk != null
      ? `(${c(x.yesBid)} + ${c(x.yesAsk)}) ÷ 2 = ${c((x.yesBid + x.yesAsk) / 2)}.`
      : null),
    seeAlso: ["price", "spread", "calibrated", "brier"],
  },
  {
    id: "spread", group: "market", term: "Spread",
    short: "The gap between buying and selling — a cost you pay the moment you enter.",
    detail:
      "Buy at the ask, sell at the bid. If the book is 73/75 you buy at 75¢ and could immediately sell at 73¢, so you " +
      "are 2¢ down before anything happens. On a 75¢ contract that is 2.7% of your stake, paid instantly.\n\n" +
      "This is why a rule that enters and exits repeatedly is expensive even when it is right about direction: it pays " +
      "the spread and the fee on every round trip.",
    worked: (x) => (x?.yesBid != null && x?.yesAsk != null
      ? `The book is ${c(x.yesBid)} / ${c(x.yesAsk)} — a spread of ${c(x.yesAsk - x.yesBid)}.`
      : null),
    seeAlso: ["price", "fee", "book"],
  },
  {
    id: "fee", group: "market", term: "Kalshi fee",
    short: "0.07 × price × (1 − price) per contract, charged when you buy AND when you sell.",
    detail:
      "The single most important number on this tab, because it is where the desk's money actually went: of Ⓩ137 lost " +
      "over 347 wagers, Ⓩ110 was fees.\n\n" +
      "The shape matters. The fee is largest at 50¢ (a coin flip) and shrinks toward both ends. But what you care about " +
      "is the fee as a share of what you STAKE, and that is 0.07 × (1 − price), which falls steadily as the price " +
      "rises: 5.25% of your stake at 25¢, 2.10% at 70¢, 0.70% at 90¢.\n\n" +
      "That is the entire reason the desk now refuses to buy anything under 70¢. It is not a forecast — it is " +
      "arithmetic. With no edge, the expected result of a wager is exactly minus the fee, so the cheap end of the book " +
      "is the expensive end to trade.\n\n" +
      "One honest caveat: the real exchange rounds the fee up to the next cent per order, so a live account pays " +
      "slightly more than this on small positions. The desk charges the exact fraction, and every figure on this tab " +
      "is the desk's — so this tab slightly UNDERSTATES what real trading would cost, never the reverse.",
    formula: "fee = 0.07 × p × (1 − p) per contract, each way (the desk charges the exact fraction)",
    worked: (x) => {
      if (x?.yesAsk == null) return null;
      const f = fee1(x.yesAsk);
      return `At ${c(x.yesAsk)} the fee is 0.07 × ${d2(x.yesAsk)} × ${d2(1 - x.yesAsk)} = ${c(f)} a contract — ` +
        `${((0.07 * (1 - x.yesAsk)) * 100).toFixed(2)}% of what you stake, and that again if you sell before the bell.`;
    },
    seeAlso: ["allin", "floor", "price", "holdtest"],
  },
  {
    id: "maker", group: "market", term: "Maker vs taker — the fee the desk pays",
    short: "Hit the ask and pay 0.07·p·(1−p). Rest at the bid and pay a quarter of that, and no spread.",
    detail:
      "Kalshi charges two very different fees for the same contract. A TAKER order fills immediately against what is " +
      "already on the book — you buy at the ask — and pays 0.07 × p × (1 − p) a contract: 1.75¢ at 50¢. A MAKER order " +
      "rests on the book at your price and waits for someone to trade into it; it pays 0.0175 × p × (1 − p): 0.44¢ at " +
      "50¢. A quarter of the fee — and because you bought at the bid rather than the ask, you did not pay the spread " +
      "either.\n\n" +
      "The desk paid taker fees for its whole record, and fees were 80% of what it lost. From 2026-09-16 every ENTRY " +
      "is a resting bid at the current bid. It fills only when a later tick shows the ask has come down to it — a " +
      "conservative reading of a real fill — and if nothing trades into it within two minutes, or the rule's window " +
      "closes, it is cancelled and the stake returned. The card shows this state as “Resting”.\n\n" +
      "The catch, stated plainly: a resting bid tends to fill when the market is moving AGAINST you — the ask comes " +
      "down to your bid because the price is falling — and it does NOT fill when the move you wanted continues. The " +
      "first evening of maker entries proved it: every momentum bid was cancelled unfilled while the price ran away " +
      "from it. So the momentum rule TAKES, at the ask, taker fee — its premise is that the move continues, and a bid " +
      "resting behind a move is self-defeating. The edge rules still rest. Exits still take: a stop that rests and " +
      "never fills is a position left to the bell.",
    formula: "taker = 0.07 × p × (1 − p) · maker = 0.0175 × p × (1 − p), per contract",
    seeAlso: ["fee", "spread", "decision", "sizing"],
  },
  {
    id: "sizing", group: "decision", term: "Stake sizing (Ⓩ5, flat — the Kelly ramp underneath)",
    short: "Every entry is Ⓩ5, by the user's call. Underneath sits a Kelly ramp that can size Ⓩ5–Ⓩ10 by edge when its cap is raised.",
    detail:
      "The user's ask, 2026-09-16: let the agent choose between Ⓩ5 and Ⓩ10 with real arithmetic. The arithmetic is " +
      "Kelly's. For a contract at price c that pays 1, the fraction of a bank that maximises long-run growth is " +
      "f* = p − (1 − p) × c ÷ (1 − c): the probability of winning, less the probability of losing scaled by the odds " +
      "you are laying. When p equals the price it is zero; it grows with the edge.\n\n" +
      "The desk maps that onto a range: the minimum at zero edge, the maximum at a full 10% Kelly fraction, a straight " +
      "line between. On a 75¢ contract a 2-point edge is already f* = 0.08, so the ramp is steep — and because a " +
      "calibrated probability sits within a point or two of the market, it mostly sits near the minimum anyway.\n\n" +
      "Later the same day the user set the stake to Ⓩ5 flat: “buying five dollars at one time”. So the cap sits on " +
      "the floor and every entry is Ⓩ5. The ramp is still in the code and still tested; raising one constant is the " +
      "whole change to size by edge again.",
    formula: "f* = p − (1 − p) × c ÷ (1 − c)\nstake = min + (max − min) × clamp(f* ÷ 0.10, 0, 1) — and today max = min = Ⓩ5",
    worked: (x) => {
      const a = x?.agentKey, p = x?.raw?.[a], m = x?.marketP, c = x?.yesAsk;
      if (p == null || m == null || c == null) return null;
      const cal = 0.25 * p + 0.75 * m;
      const f = cal - (1 - cal) * c / (1 - c);
      const ramp = 5 + 5 * Math.max(0, Math.min(1, f / 0.10));
      return `Calibrated ${Math.round(cal * 100)}% against a ${Math.round(c * 100)}¢ ask: f* = ${f.toFixed(3)}. The ramp would say Ⓩ${ramp.toFixed(2)}; the desk stakes Ⓩ5.00 flat.`;
    },
    seeAlso: ["calibrated", "agrees", "bank", "stake"],
  },
  {
    id: "allin", group: "market", term: "All-in cost a contract",
    short: "The price plus the fee — what one contract really costs you.",
    detail:
      "The price on its own flatters every trade, because the fee is charged separately and is invisible in a " +
      "percentage. This line adds them together so the cost you see is the cost you pay.\n\n" +
      "Every payout multiple on this tab is quoted on this all-in figure rather than the bare price, so it can never " +
      "make a trade look better than it is.",
    formula: "all-in = price + fee",
    worked: (x) => (x?.yesAsk != null ? `${c(x.yesAsk)} + ${c(fee1(x.yesAsk))} fee = ${c(x.yesAsk + fee1(x.yesAsk))} a contract.` : null),
    seeAlso: ["fee", "multiple", "contracts"],
  },
  {
    id: "multiple", group: "market", term: "Payout multiple (“1.32x · pays $1”)",
    short: "How many times your all-in cost comes back if you are right.",
    detail:
      "Every contract pays exactly $1 if it wins. So if it costs you 76¢ all-in, being right returns 1 ÷ 0.76 = 1.32 " +
      "times your money — a 32% gain. Being wrong returns nothing at all.\n\n" +
      "A high multiple is not a good deal, it is a cheap contract, and cheap contracts are cheap because they usually " +
      "lose. A 4x multiple means the market thinks you will be right about a quarter of the time, and the market has " +
      "been right about that more consistently than any signal this desk has tested.",
    formula: "multiple = 1 ÷ all-in cost",
    worked: (x) => {
      if (x?.yesAsk == null) return null;
      const a = x.yesAsk + fee1(x.yesAsk);
      return `1 ÷ ${d2(a)} = ${d2(1 / a)}x. Right returns ${d2(1 / a)}× your outlay; wrong returns nothing.`;
    },
    seeAlso: ["allin", "payout", "contracts"],
  },
  {
    id: "contracts", group: "market", term: "Contracts",
    short: "Your stake divided by the price — fractional, and each pays $1 if right.",
    detail:
      "The desk stakes a fixed Ⓩ5 per wager, so the number of contracts it gets depends on the price. At 75¢ that is " +
      "5 ÷ 0.75 = 6.67 contracts; at 25¢ it would be 20. Kalshi settles fractional contracts, so this is not rounded.\n\n" +
      "This is why a cheap contract is not “more upside for the same money” in any useful sense — you get more of them, " +
      "but each is less likely to pay, and you are charged a fee on every one.",
    formula: "contracts = stake ÷ price",
    worked: (x) => {
      if (x?.yesAsk == null) return null;
      const s = Number(x.stake || 5);
      const n = s / x.yesAsk;
      return `${d2(s)} ÷ ${d2(x.yesAsk)} = ${d2(n)} contracts, paying ${d2(n)} if Up wins.`;
    },
    seeAlso: ["stake", "payout", "fee"],
  },
  {
    id: "payout", group: "market", term: "Ⓩ5 → payout, and “net if right”",
    short: "What a Ⓩ5 stake returns if the side wins, and the profit after all costs.",
    detail:
      "The first figure is the gross return: your contracts each pay $1. The second, smaller line is what you actually " +
      "keep — gross minus your stake minus the fee.\n\n" +
      "Both are shown because the gross number alone is how prediction markets are usually sold, and it consistently " +
      "makes a marginal trade look like a good one.",
    formula: "net = contracts − stake − fee(price, contracts)",
    worked: (x) => {
      if (x?.yesAsk == null) return null;
      const s = Number(x.stake || 5);
      const n = s / x.yesAsk;
      const f = fee1(x.yesAsk, n);
      return `${d2(n)} contracts pay ${d2(n)}; minus the ${d2(s)} staked and ${d2(f)} fee leaves ${d2(n - s - f)} net.`;
    },
    seeAlso: ["contracts", "multiple", "fee"],
  },
  {
    id: "book", group: "market", term: "Order book",
    short: "The resting orders: what people will pay, and what they will sell for.",
    detail:
      "Bids are what buyers are offering; asks are what sellers want. The contract counts show how much size sits at " +
      "each level, which is how you tell a real price from a lonely one.\n\n" +
      "The desk always fills at the live book rather than the last traded price. Its first night of trading did the " +
      "opposite — it filled at a lagging quote from the market list — and that record is kept in a separate era " +
      "precisely because those fills were not real.",
    seeAlso: ["spread", "price", "eras"],
  },

  // ── the agents ──────────────────────────────────────────────────────────
  {
    id: "raw", group: "agents", term: "Raw P(Up)",
    short: "What an agent's own method thinks, before any correction.",
    detail:
      "Each of the six agents has a different way of turning the live price into a probability. All of them start from " +
      "the same fair value and then apply their own tilt:\n\n" +
      "• Atlas — fair value, untouched.\n" +
      "• Comet — fair value tilted toward recent momentum.\n" +
      "• Drift — fair value tilted AGAINST recent momentum.\n" +
      "• Ledger — fair value tilted by order-flow imbalance.\n" +
      "• Kestrel — fair value recomputed with recent volatility instead of the long-run figure.\n" +
      "• Echo — halfway between fair value and the market price.\n\n" +
      "Raw is shown so you can see what each method genuinely believes. It is not what it trades on.",
    seeAlso: ["calibrated", "fairvalue", "z"],
  },
  {
    id: "calibrated", group: "agents", term: "Calibrated P(Up)",
    short: "The raw view corrected toward the market — this is what it trades on.",
    detail:
      "Left alone, every method on this desk was worse than the market price at every checkpoint. So each one is " +
      "corrected before it is allowed to act.\n\n" +
      "By default the correction is a shrink: 25% of the agent's own view, 75% of the market. An agent may instead earn " +
      "a fitted correction — a logistic blend of its view and the market's — but only if that fit beats the plain " +
      "shrink on the most recent 30% of windows it has NOT been trained on, with at least 300 windows to train on. " +
      "A fit on 114 windows was tried, and was worse than the shrink out of sample at every minute, which is why the " +
      "bar is set where it is.\n\n" +
      "A consequence worth understanding: under the shrink, a calibrated probability can never stray more than 25 " +
      "points from the market. An agent cannot “strongly disagree” — by design.",
    formula: "shrink: p = 0.25 × raw + 0.75 × market\nfitted: p = sigmoid(a·logit(raw) + b·logit(market) + c)",
    worked: (x) => {
      const a = x?.agentKey, r = x?.raw?.[a], m = x?.marketP;
      if (r == null || m == null) return null;
      return `0.25 × ${p(r)} + 0.75 × ${p(m)} = ${p(0.25 * r + 0.75 * m)}.`;
    },
    seeAlso: ["raw", "mid", "holdout", "brier"],
  },
  {
    id: "fairvalue", group: "agents", term: "Fair value",
    short: "The chance of finishing above the target if Bitcoin just wanders from here.",
    detail:
      "The desk's base model, and the starting point for every agent. It assumes Bitcoin has no direction — that from " +
      "here it is equally likely to drift up or down — and asks only: given how far it currently sits from the target, " +
      "and how much time is left to move, what fraction of random paths finish above?\n\n" +
      "That is a deliberately humble model. It contains no view about Bitcoin at all. Its only inputs are distance and " +
      "time remaining, and it turns out that adding views to it has consistently made it worse.",
    formula: "fair value = Φ(z), the normal curve evaluated at z",
    seeAlso: ["z", "sigma", "raw"],
  },
  {
    id: "z", group: "agents", term: "z (the gap in sigmas)",
    short: "The gap to the target measured in units of how far Bitcoin can still move.",
    detail:
      "The number that makes the dollar gap meaningful. It divides the distance to the target by how much Bitcoin " +
      "could plausibly move in the time remaining. z = 1 means the target is exactly one typical move away; z = 3 " +
      "means it would take an unusual move to get there.\n\n" +
      "Because the time term shrinks as the bell approaches, the same dollar gap produces a steadily larger z through " +
      "the window — which is exactly why prices run toward 0 or 100 late in a window even when the price is not moving.",
    formula: "z = ln(price ÷ target) ÷ (σ₁₅ × √(remaining variance))",
    worked: (x) => {
      if (x?.btc == null || x?.target == null || x?.sigma15 == null) return null;
      const m = Math.max(0, Math.min(15, Number(x.minute) || 0));
      const frac = m <= 14 ? ((14 - m) + 1 / 3) / 15 : Math.max(((15 - m) ** 3) / 3 / 15, 0.0005);
      const z = Math.log(x.btc / x.target) / (x.sigma15 * Math.sqrt(frac));
      return `ln(${usd(x.btc)} ÷ ${usd(x.target)}) ÷ (${x.sigma15} × √${d2(frac)}) = z of ${d2(z)}.`;
    },
    seeAlso: ["fairvalue", "sigma", "gap"],
  },
  {
    id: "sigma", group: "agents", term: "σ₁₅ (sigma)",
    short: "How much Bitcoin typically moves in 15 minutes — the desk's ruler.",
    detail:
      "0.00212, or about a fifth of one percent, measured over 14 August to 13 September 2026. It is the unit " +
      "everything else is expressed in: a “1σ move” means a move of that size.\n\n" +
      "Kestrel is the one agent that questions it, substituting recent realised volatility when the tape is unusually " +
      "fast or slow, on the theory that a month-old average is the wrong ruler during a burst. That theory has not yet " +
      "had enough windows to be judged.",
    seeAlso: ["z", "fairvalue", "kestrel-rule"],
  },

  // ── the decision ────────────────────────────────────────────────────────
  {
    id: "decision", group: "decision", term: "How an agent actually decides",
    short: "Eight gates, in order. A wager happens only if every one of them passes.",
    detail:
      "Every 20 seconds the desk re-runs this for each agent against each rule. Nothing is on a schedule; a wager " +
      "happens when the gates open and not otherwise.\n\n" +
      "1. READ. Take the live Bitcoin price and the live Kalshi order book.\n\n" +
      "2. PRICE IT. Compute z, then fair value, then each agent's own raw probability from its own tilt.\n\n" +
      "3. CORRECT IT. Shrink each raw view toward the market — 25% its own, 75% the market's — unless that agent has " +
      "earned a fitted correction on held-out windows.\n\n" +
      "4. IS THE RULE OPEN? Each rule only acts inside its own minutes of the window, is refused if it has been " +
      "retired or stopped, and may hold only one position per agent at a time. There is no limit on how many times " +
      "it may go at one window — only a one-minute cooldown after any exit.\n\n" +
      "5. DOES THE RULE TRIGGER? The rule's own condition — a gap of 0.8σ, a 1σ move in five minutes, volatility 1.5× " +
      "normal, a price above 85¢. If it does not trigger, nothing happens.\n\n" +
      "6. DOES THIS AGENT AGREE? The decisive gate. The agent's OWN corrected probability must rate that side higher " +
      "than the market charges for it. Without this, a rule that reads only the tape produces six identical wagers " +
      "from six agents — one decision wearing six names. With it, the agents genuinely split.\n\n" +
      "7. IS THE PRICE SANE? The contract must cost at least 70¢ and no more than the rule's own ceiling. Edge rules " +
      "must additionally beat the ask by the fee plus 5¢.\n\n" +
      "8. CAN IT AFFORD IT? The agent's bank must be above 20% of where it started.\n\n" +
      "Only then does it buy: stake ÷ price contracts, at the live ask, fee paid.",
    seeAlso: ["agrees", "floor", "minedge", "shots", "bankfloor", "retired"],
  },
  {
    id: "agrees", group: "decision", term: "“Agrees” — the independence gate",
    short: "The agent's own probability must beat what the market charges for that side.",
    detail:
      "This exists because of a real bug. All six agents once sold the same rule at the same price in the same second " +
      "for the same profit — because the rule read only the tape, and the tape says one thing to everyone. That is not " +
      "six decisions, it is one decision copied six times, and it makes a record look six times more convincing than " +
      "it is.\n\n" +
      "Now every rule-based entry must also clear the acting agent's own view: it must think that side is underpriced. " +
      "Since the corrected probability is 25% the agent's own view and 75% the market's, this works out to a clean " +
      "test — the agent enters exactly when its RAW view beats the market. Momentum-tilted Comet and mean-reverting " +
      "Drift therefore disagree by construction, and whoever disagrees sits the trade out.\n\n" +
      "Measured across 882 checkpoints after the change: the agents diverged 56% of the time, against 100% unanimity " +
      "before it.",
    formula: "enter only if p(side) > market price of that side + 1 point, and p is not on the 2%/98% clip",
    worked: (x) => (x?.marketP != null && x.marketP <= 0.03
      ? `The market is at ${Math.round(x.marketP * 100)}%. Every agent's number is clipped at 2%, which is not a view — so no agent backs a side here, whatever the sign of the difference.`
      : null),
    seeAlso: ["decision", "calibrated", "raw"],
  },
  {
    id: "floor", group: "decision", term: "Entry price floor (70¢)",
    short: "Nothing cheaper than 70¢ may be bought, because the fee there is too large a share of the stake.",
    detail:
      "Raised from 25¢ on 2026-09-16, and this one is arithmetic rather than a backtest.\n\n" +
      "The fee as a share of your stake is 0.07 × (1 − price). At 25¢ that is 5.25%; at 70¢ it is 2.10%; at 90¢ it is " +
      "0.70%. With no measurable edge, the expected result of any wager is exactly minus the fee — so the cheap end of " +
      "the book is the expensive end to trade.\n\n" +
      "The record agreed before the arithmetic was applied: the 25–50¢ band lost Ⓩ111 across 169 wagers, while the " +
      "only profitable band on the whole desk was 75¢ and above.\n\n" +
      "The floor does not create profit. Nothing here does. It makes the unavoidable loss as small as the fee schedule " +
      "allows.",
    seeAlso: ["fee", "decision", "edge"],
  },
  {
    id: "minedge", group: "decision", term: "Minimum edge (5¢)",
    short: "An edge rule must beat the ask by the fee plus 5¢ before it may buy.",
    detail:
      "Applies to the two rules that trade on a probability rather than a pattern — the gap rule and the volatility " +
      "rule. It is not enough to think a contract is slightly underpriced; the gap has to cover the fee and then clear " +
      "a further 5¢, or the trade is not worth its costs.\n\n" +
      "In practice this is a very high bar, and deliberately so. Above the 70¢ floor an edge entry needs a probability " +
      "of roughly 82% against a 75¢ ask — and under the standard shrink a corrected probability tops out at 0.25 + " +
      "0.75 × market. Those two facts together mean the edge rules are usually dormant. They are not broken; they are " +
      "correctly declining to trade on a disagreement they cannot actually justify.",
    formula: "enter only if p − ask > 0.05 + fee(ask)",
    seeAlso: ["floor", "calibrated", "decision"],
  },
  {
    id: "shots", group: "decision", term: "Shots, cooldown and open positions",
    short: "One live position per rule, as many goes at a window as the signal gives it, one minute between them.",
    detail:
      "A rule may re-enter a window after banking a profit, which is what lets a fast move be harvested more than " +
      "once. There is no cap on how many times it may go at a single window — sell into a profit, and if the signal " +
      "comes back, go again. Two limits keep that from becoming churn:\n\n" +
      "• one live position per agent per rule at a time;\n" +
      "• at least one minute after an exit before the same rule may go again.\n\n" +
      "Without the cooldown a rule would pay the spread and the fee repeatedly on a single wobble, which is the most " +
      "reliable way to lose money on a market with no edge in it.\n\n" +
      "Worth watching, because the record does not yet support more trades being better trades: since the desk was " +
      "reset, first shots have averaged +Ⓩ0.36 and second shots −Ⓩ1.23. That is only three second shots, far too " +
      "few to conclude from, but every wager carries its shot number so the question stays answerable.",
    seeAlso: ["decision", "spread", "fee"],
  },
  {
    id: "bankfloor", group: "decision", term: "Bank floor (20%)",
    short: "An agent that has lost 80% of its bank stops entering.",
    detail:
      "Each agent starts with Ⓩ500 of its own paper money and stops taking new positions if that falls below Ⓩ100.\n\n" +
      "A daily staking cap was considered instead and rejected: an agent can turn over Ⓩ960 in a day at two entries a " +
      "window, so a turnover limit would silence a WINNING agent by lunchtime purely for being busy. What actually " +
      "needs bounding is drawdown, so the guard is a floor under the bank. An agent that reaches it has produced a real " +
      "result worth reporting, not a throttle worth working around.",
    seeAlso: ["bank", "decision"],
  },
  {
    id: "retired", group: "decision", term: "Retired vs stopped",
    short: "Stopped is a verdict on a record. Retired means the rule's premise was disproved.",
    detail:
      "A STOPPED rule has been judged on 200 windows and found to lose. Rewrite it and bump its version and it gets a " +
      "fresh trial, because the verdict was about that version's record.\n\n" +
      "A RETIRED rule is different: the thing it claimed about the world was measured directly and is not there. No " +
      "record can acquit it, so nothing reopens it — not a new version, and not a failed database read. It is refused " +
      "at the point of entry rather than filtered downstream.\n\n" +
      "Two rules are currently retired. “Follow the flow” claimed order flow leads the Kalshi price; regressing the " +
      "next move in the price on the preceding Coinbase move across 1,826 tick pairs gave +0.67¢ per σ with a 95% " +
      "interval of −1.38¢ to +2.72¢ — indistinguishable from nothing. It had also lost Ⓩ147 across 284 wagers, in " +
      "every price band, paying Ⓩ105 of the desk's Ⓩ110 in fees. “Fade the stretch” is retired because its 25–40¢ " +
      "band now sits entirely below the floor, and that band is where most of the desk's money died.\n\n" +
      "Retired rules stay visible, with their reason attached. A rule that simply vanished would teach nothing, and " +
      "the reason IS the finding.",
    seeAlso: ["judgement", "decision", "floor"],
  },

  // ── exits ───────────────────────────────────────────────────────────────
  {
    id: "mark", group: "exits", term: "Mark",
    short: "What the position would fetch if sold into the book right now.",
    detail:
      "Kalshi contracts can be sold before the bell, so an open position has a live value. The mark is what you would " +
      "actually receive — you sell into the BID, not the mid — less the exit fee.\n\n" +
      "Every open position is marked on every tick. The figure beside an open wager is unrealised: it is what the " +
      "position is worth now, not what it will settle at.",
    formula: "mark = yes bid (for Up) or 1 − yes ask (for Down)\nunrealised = contracts × mark − exit fee − (stake + entry fee)",
    worked: (x) => {
      if (x?.yesAsk == null || x?.yesBid == null) return null;
      const s = Number(x.stake || 5), n = s / x.yesAsk;
      const cost = s + 0.07 * x.yesAsk * (1 - x.yesAsk) * n;
      const now = n * x.yesBid - 0.07 * x.yesBid * (1 - x.yesBid) * n - cost;
      return `Buy ${n.toFixed(2)} contracts at the ${(x.yesAsk * 100).toFixed(0)}¢ ask and the position is marked at the ` +
        `${(x.yesBid * 100).toFixed(0)}¢ bid the same instant: ${now.toFixed(2)} — in the hole by the entry fee, the exit fee ` +
        `it would pay to leave, and the spread, before the price has moved at all.`;
    },
    seeAlso: ["spread", "fee", "settlement", "trail", "grossnet"],
  },
  {
    id: "take", group: "exits", term: "Take-profit",
    short: "A fixed gain at which the rule sells — unless the move is still running, or the price is far past the target.",
    detail:
      "The momentum rule sells once it is Ⓩ1 up net of both fees. Two things override that and make it hold:\n\n" +
      "• the last five minutes are still a full σ in its favour — a move still running is not sold for a ZBuck " +
      "(the user's read of the chart, 2026-09-16);\n" +
      "• the price is more than $50 past the target in the position's favour, which is the user's call of " +
      "2026-09-17: a contract that far in the money is likely to stay there, so hold it and let the trail decide.\n\n" +
      "Neither override touches the stop-loss, the trailing stop or the before-the-bell exit, so a position that " +
      "turns is still sold. What holding gives up is certainty; what it buys is the rest of the move — the take fires " +
      "around 87¢, and a contract that settles is worth 100¢.\n\n" +
      "One caveat worth carrying: $50 is not the same bet at every minute. With thirteen minutes left it is a third " +
      "of a σ and the position is about 63% likely to still be on the right side at the bell; with one minute left it " +
      "is two σ and 98% likely. So the rule holds hardest early, where it is least certain.",
    seeAlso: ["stop", "trail", "mark", "sigma"],
  },
  {
    id: "stop", group: "exits", term: "Stop-loss",
    short: "A fixed loss in cents at which the rule gives up.",
    detail:
      "The counterpart to the take-profit: the momentum rule exits once the price is 15¢ against it — a gross move " +
      "that nets about −18¢ a contract once both fees are in. Selling a losing position pays a " +
      "second fee, so a stop is not free — it is a judgement that the position has told you something and holding it to " +
      "the bell is worse than paying to leave.\n\n" +
      "That judgement was tested rather than assumed. See “Why the stops stay”.",
    seeAlso: ["take", "trail", "holdtest"],
  },
  {
    id: "trail", group: "exits", term: "Trailing stop",
    short: "Ride the move, and leave once it hands back 40% of its best gain — but never before the take-profit has had its chance.",
    detail:
      "This is what “sell near the high” means without hindsight. The desk tracks the best gain a position has reached " +
      "and sells if it gives back 40% of it. A position that runs to 20¢ up is sold if it falls back to 12¢. It never " +
      "catches the exact top — nothing does — but it turns a move into money without needing to know when the move ends.\n\n" +
      "WHEN IT STARTS WATCHING changed on 2026-09-17. It used to arm at 4% of the stake, which on a Ⓩ10 trade is " +
      "Ⓩ0.40 — while the take-profit sits at Ⓩ1.00. So the trail was free to sell a winner that had not yet had a " +
      "chance to reach its own target, and the record showed it doing exactly that: across 218 wagers the take-profit " +
      "earned Ⓩ1.29 a fire, entering at 76.9¢ and selling at 98.0¢, while the trailing stop fired nearly three times " +
      "as often for Ⓩ0.06, peaking at 90.5¢ and selling at 84.3¢ — Ⓩ0.38 a trade handed back, Ⓩ18.60 over 49 trades, " +
      "with Ⓩ5.96 of fees paid to do it.\n\n" +
      "It now arms no earlier than 80% of the rule's own take-profit, which makes it a protector of large gains rather " +
      "than a competitor to the take. A rule that has no take-profit at all — where the trail IS the profit exit — " +
      "keeps the old stake-based arm, because for those there is nothing to cut short.",
    seeAlso: ["take", "stop", "mark"],
  },
  {
    id: "holdtest", group: "exits", term: "Why the stops stay",
    short: "Holding everything to settlement was tested and is Ⓩ85 worse.",
    detail:
      "The obvious theory was that exiting early burns a second fee, and the desk would do better holding to the bell. " +
      "It was tested against every real wager using the real outcomes, and it is wrong:\n\n" +
      "• as actually traded, with all the exits: −Ⓩ147\n" +
      "• if every position had been held to settlement: −Ⓩ233\n" +
      "• if nothing had been traded at all: Ⓩ0\n\n" +
      "The stops are saving Ⓩ85. The entries were the problem, not the exits — which is why the fix was a price floor " +
      "and two retirements, and why every trailing stop was left exactly where it was.\n\n" +
      "Note the third line, which is the honest one: not trading beat both.",
    seeAlso: ["trail", "floor", "edge"],
  },
  {
    id: "grossnet", group: "exits", term: "Gross vs net — where the fee sits",
    short: "P&L is net from the first tick. The exit triggers are gross price moves, and net less.",
    detail:
      "Two different things on this tab carry the fee in two different places, and it is worth knowing which is which.\n\n" +
      "Every P&L figure — on an agent card, in the record, in a pop card — is NET. The cost of a wager is the stake " +
      "plus the entry fee, and the mark subtracts the exit fee it would pay to leave, so a position opens already in " +
      "the hole by both fees and the spread. It does not start at zero and drift down; it starts down and has to climb " +
      "out. That is the honest accounting and it is what the desk does.\n\n" +
      "The exit TRIGGERS are the exception: they are measured as a raw move in the contract price. “Take profit at " +
      "+20¢” fires when the price is 20¢ above entry, not when the net gain is 20¢. So a +20¢ exit nets about 18¢ at a " +
      "75¢ entry and about 16½¢ at a 50¢ one; a −15¢ stop nets about −18¢; and the trailing stop arms at a +6¢ move " +
      "that is only +3½¢ net at 75¢ and barely +2½¢ at 50¢.\n\n" +
      "The last one matters. Arming on a gross gain means the trail can fire to “protect” a profit that, after fees, " +
      "is not one. Across the record, trailing stops that sold at a gross gain but a net loss are a real, counted " +
      "set — see the worked figure. Whether a net-aware arm would do better is not answerable from the peaks alone, " +
      "because it changes when the trail starts, not just where it ends.",
    formula: "trigger: mark − entry ≥ threshold (gross)\nP&L: contracts × mark − exit fee − stake − entry fee (net)",
    seeAlso: ["mark", "trail", "take", "stop", "fee"],
  },
  {
    id: "closereason", group: "exits", term: "Close reason",
    short: "Why a position ended: take profit, stop, trail, before the bell, or settlement.",
    detail:
      "“settlement” means it was held to the bell and paid $1 or $0 — only the control does that now. Everything else " +
      "means it was sold into the book at the mark, paying a second fee. “before bell” is the user's rule: at minute " +
      "14.5 every other position is sold whatever it is doing. A winner sold at 97¢ forgoes the last cents and pays " +
      "the exit fee; a loser sold at 3¢ recovers them. What that nets over many windows is what the record measures.\n\n" +
      "Reading these down the record is the fastest way to see what a rule actually does, as opposed to what its " +
      "description claims it does.",
    seeAlso: ["take", "stop", "trail", "settlement"],
  },

  // ── the record ──────────────────────────────────────────────────────────
  {
    id: "pnl", group: "record", term: "P&L",
    short: "Money made or lost, after both fees.",
    detail:
      "Always net. Entry fee, exit fee if it sold, and the stake itself are all accounted for before this number is " +
      "shown. There is no gross figure anywhere on this tab, because a gross figure on this desk would be positive " +
      "while the desk lost money.",
    seeAlso: ["ror", "fee", "winrate"],
  },
  {
    id: "ror", group: "record", term: "Return on risk",
    short: "P&L as a percentage of everything staked — the number rules are judged on.",
    detail:
      "Win rate tells you how often; this tells you whether it was worth it. A rule that wins 90% of the time and " +
      "loses money is entirely possible here, and the favourite-late rule is close to exactly that.\n\n" +
      "This is the figure a rule is judged on after 200 windows, not win rate, which was the user's original target " +
      "and which the record showed to be the wrong measure.",
    formula: "return on risk = P&L ÷ total staked",
    seeAlso: ["winrate", "judgement", "pnl"],
  },
  {
    id: "winrate", group: "record", term: "Win rate",
    short: "How often it was right — never shown without P&L beside it.",
    detail:
      "Deliberately never displayed alone anywhere on this tab. Buying heavy favourites produces a win rate near 90% " +
      "and still loses money after fees, so a high win rate presented on its own is closer to a lie than a fact.\n\n" +
      "Every place this appears, return on risk or P&L appears beside it.\n\n" +
      "It is also not ONE measure on this desk, which is what makes two agents' win rates look wildly apart when they " +
      "are doing the same thing. A position HELD to the bell wins when the contract settles in the money: buy a 90¢ " +
      "favourite and you win about 90% of the time by construction, and you must clear 90.6% — the price plus its fee — " +
      "just to break even. A position SOLD early wins when it was exited ahead, and that has no such floor: a rule can " +
      "make money exiting ahead 57% of the time if its losses are cut small.\n\n" +
      "So the scoreboard says which: a held book shows the rate it NEEDS beneath its rate, and turns red when it is " +
      "under it. An agent holding favourites and an agent scalping momentum cannot be compared on win rate at all. " +
      "Compare them on return on risk.",
    seeAlso: ["ror", "pnl", "echo-rule"],
  },
  {
    id: "brier", group: "record", term: "Brier score",
    short: "How wrong the probabilities were, squared. Lower is better.",
    detail:
      "Say 70% and Up happens: you were 0.30 wrong, scored as 0.09. Say 70% and it does not: 0.70 wrong, scored as " +
      "0.49. Averaged over many windows, this rewards being confident when right and punishes it severely when wrong.\n\n" +
      "It is the fairest way to compare a forecaster to the market, because it grades the probability rather than the " +
      "call. The table shows each agent's calibrated and raw scores against the market's at each checkpoint, and green " +
      "means the agent beat the market there.\n\n" +
      "The uncomfortable summary: over the desk's history, every method scored worse than the market at every minute.",
    formula: "Brier = (probability − outcome)², averaged",
    seeAlso: ["calibrated", "curve", "mid"],
  },
  {
    id: "curve", group: "record", term: "Reliability curve (“said → came true”)",
    short: "When it said 70%, did Up happen about 70% of the time?",
    detail:
      "Bucket every forecast by what it said, then check what actually happened in each bucket. A perfectly calibrated " +
      "forecaster sits on the tick every time: when it says 70%, Up comes true 70% of the time.\n\n" +
      "The bar is what happened; the vertical tick is where perfect calibration would be. Bars falling short of the " +
      "tick mean overconfidence. The n beside each row is how many windows are in that bucket — a bucket with n under " +
      "about 30 is not really evidence of anything.",
    seeAlso: ["brier", "calibrated"],
  },
  {
    id: "holdout", group: "record", term: "Holdout / “did not beat the shrink”",
    short: "A fitted correction is only used if it beats the simple one on windows it never saw.",
    detail:
      "Anything can be fitted to the past. The test that matters is whether it survives contact with data it was not " +
      "trained on.\n\n" +
      "So a fitted correction must train on at least 300 windows and then beat the plain 75% shrink on the most recent " +
      "30%, which are held back. When the tab says a fit “did not beat the shrink”, one was tried and failed that " +
      "test, so the simpler correction is still in use.\n\n" +
      "This rule exists because a fit on 114 windows was worse than the shrink at every single minute on the windows " +
      "that followed.",
    seeAlso: ["calibrated", "brier"],
  },
  {
    id: "judgement", group: "record", term: "Judgement (“measuring 42/200”)",
    short: "A rule is judged after 200 distinct WINDOWS, on return on risk.",
    detail:
      "The count is windows, not wagers, and the difference is the whole point. Six agents acting on one rule-based " +
      "signal produce six wagers carrying the information of ONE outcome. Counting wagers would reach a verdict six " +
      "times too early on the same evidence, and would make a coin flip look like a trend.\n\n" +
      "After 200 windows a rule is graduated if its return on risk is positive and stopped if it is not. Until then it " +
      "is measuring, and the bar shows how far along it is.",
    seeAlso: ["ror", "windows", "graduation", "retired"],
  },
  {
    id: "windows", group: "record", term: "Windows vs wagers",
    short: "Six agents copying one signal are one piece of evidence, not six.",
    detail:
      "The most important statistical idea on this tab, and the reason several numbers here look smaller than you might " +
      "expect.\n\n" +
      "When a rule fires on the tape, every agent that agrees takes it — so one signal can produce six wagers that all " +
      "win or all lose together. They are not independent samples. Treating them as six would shrink every confidence " +
      "interval by a factor of about √6 and dress noise up as a finding.\n\n" +
      "So everything that is meant to be evidence — the judgement count, the daily projection, the confidence ranges — " +
      "counts distinct windows. Wager counts are shown as activity, never as proof.",
    seeAlso: ["judgement", "projection"],
  },
  {
    id: "eras", group: "record", term: "Eras",
    short: "Three separate records, kept apart because they are not comparable.",
    detail:
      "• FIRST NIGHT — filled at a lagging quote from the market list rather than the live book. Those fills were not " +
      "real prices, so that record is not evidence of anything and is kept only for honesty.\n\n" +
      "• TILT — 14 to 16 September, 509 wagers, 37% won, −Ⓩ306. The record that caused the desk to be rebuilt: every " +
      "method's Brier was worse than the market's, and three-quarters of the loss was minute-13 longshots under 30¢, " +
      "which won 9% of the time.\n\n" +
      "• STRATEGY — the current desk, with calibration, price floors, real exits and per-rule judgement. This is the " +
      "only era that counts toward graduation.\n\n" +
      "They are never merged into a single number, because doing so would let a fixed mistake keep polluting the " +
      "verdict on the fix.",
    seeAlso: ["judgement", "book"],
  },
  {
    id: "projection", group: "record", term: "24-hour projection",
    short: "What the last day's rate extrapolates to over 96 windows, with the range around it.",
    detail:
      "There are 96 fifteen-minute windows in a day. The projection takes what each settled WINDOW earned, averages it, " +
      "and multiplies out — then puts a 95% interval around the total.\n\n" +
      "The interval is the point. If it crosses zero, the tab says outright that the desk cannot tell a profit from a " +
      "loss at this sample size, because a projection without its uncertainty is a guess wearing a decimal point. With " +
      "fewer than 30 windows it refuses to call itself a projection at all and says “placeholder”.\n\n" +
      "It extrapolates what happened. It does not forecast what will.",
    formula: "per-day = mean per window × 96, ± 1.96 × sd × √96",
    seeAlso: ["windows", "ror"],
  },

  // ── money ───────────────────────────────────────────────────────────────
  {
    id: "agentcard", group: "agents", term: "The agent card, line by line",
    short: "Everything on one agent's card, and what to read from it.",
    detail:
      "The name and the big percentage: the agent's calibrated probability that this window finishes Up, re-priced live " +
      "from the tape. Green above 50%, red below — and that colour is NOT a buy signal. Green means “more likely than " +
      "not”; whether the agent would actually buy depends on beating the PRICE, and the line under the method says so " +
      "in words: “backs Up” when its number is above the market's, “backs Down” when below. An agent can be green at " +
      "73% and lean Down, because the market says 76%. Measured on the record: Atlas was green 470 times and sat below " +
      "the market on 350 of them. Beneath the name is its METHOD — the way it turns the price into a " +
      "raw probability before correction. Beside the percentage, “−2 vs mkt” is its probability minus the market's, in " +
      "points; under the standard correction no agent can be more than 25 points away.\n\n" +
      "The sparkline is its cumulative P&L over its last forty wagers, net of every fee — a line that ends above the " +
      "zero rule is an agent that is up over that stretch. Under it, three figures: BANK is its own paper money against " +
      "the Ⓩ500 it started with; PAID is every entry and exit fee it has handed over, with that as a share of everything " +
      "it has risked as PAID; and FEES % is that as a share of everything it has risked — set it beside the return on " +
      "risk on the judgement line, because on this desk the two are close to the same number, which is the finding.\n\n" +
      "The status line says what it is doing this window — watching all six rules, holding a side in N rules with the " +
      "unrealised P&L of all of them marked to the live book, or N done with their net result. The bar at the foot is " +
      "its progress toward the 200-window judgement.\n\n" +
      "Tap the card for STRATEGY · RECORD · CALIBRATION · CHARTS. CHARTS is the full account: P&L wager by wager with " +
      "the fee drawn as the gap between the gross and net lines, what it said against what happened in every window, " +
      "and where the money went by rule, by how each position ended, and by entry price.",
    seeAlso: ["calibrated", "raw", "agrees", "bank", "fee", "ror", "judgement", "pnl"],
  },
  {
    id: "stake", group: "money", term: "Stake (Ⓩ5)",
    short: "Every wager risks the same Ⓩ5 of the agent's own paper bank.",
    detail:
      "Fixed on purpose. Varying the stake by conviction would mix two questions — is the rule right, and is the " +
      "sizing right — and make a bad rule look good if it happened to size well. A flat stake means the record is " +
      "about the rule.\n\n" +
      "With six rules per agent, an agent can have up to Ⓩ30 at risk at once.",
    seeAlso: ["contracts", "bank", "ror"],
  },
  {
    id: "bank", group: "money", term: "Bank",
    short: "Each agent's own paper money — Ⓩ500 to start, entirely separate from ZBucks.",
    detail:
      "The desk runs on its own ledger. Nothing here touches the shared ZBucks balance, in either direction, except " +
      "through graduation.\n\n" +
      "That separation exists because an earlier version let 210 coin flips pollute six agents' leaderboard records " +
      "and the graduation rung, and it had to be unwound by hand.",
    seeAlso: ["bankfloor", "graduation", "stake"],
  },
  {
    id: "graduation", group: "money", term: "Graduation to ZBucks",
    short: "The only door from this desk to real ZBucks, and it opens on return on risk.",
    detail:
      "A strategy that has traded 200 distinct windows and made money on risk is graduated. Only then is its profit " +
      "above the Ⓩ500 starting bank swept across — and it is swept as desk INCOME, never as trades, so it can never " +
      "inflate anyone's trading record.\n\n" +
      "This is the mechanism by which winning here raises real buying power. It is deliberately slow, because 200 " +
      "windows is roughly the point at which a return on risk means something rather than reflecting a good afternoon.",
    seeAlso: ["judgement", "ror", "bank"],
  },
  {
    id: "currency", group: "money", term: "Ⓩ / $ toggle",
    short: "Switches every figure on the tab between ZBucks and dollars.",
    detail:
      "A display conversion only — the desk's ledger is in ZBucks and nothing is converted behind the scenes. Prices " +
      "in cents are Kalshi's own contract prices and do not change with the toggle, because they are probabilities.",
    seeAlso: ["bank", "stake"],
  },

  // ── the rules themselves ────────────────────────────────────────────────
  {
    id: "echo-rule", group: "decision", term: "The control (“the favourite, late”)",
    short: "Buys anything above 85¢ from minute 10 and holds it. The honest baseline.",
    detail:
      "Deliberately the dumbest rule on the desk: no model, no exit, no cleverness. From minute 10 it buys whichever " +
      "side the market has already decided and holds to the bell.\n\n" +
      "It exists as a control. Every other rule is trying to beat this, and if a rule with a model cannot beat buying " +
      "the favourite and sitting still, the model is not worth its fees. It runs for one agent only — six copies would " +
      "multiply the stake without adding a single bit of information.\n\n" +
      "It is also, uncomfortably, the only rule that has made money: +Ⓩ4.6 across 50 wagers. Its win rate is near 90% " +
      "and its margin is thin, which is exactly what the original study predicted.",
    seeAlso: ["winrate", "knownsofar", "judgement"],
  },
  {
    id: "kestrel-rule", group: "decision", term: "The volatility rule",
    short: "Only acts when the last hour has been 1.5× more volatile than the long-run average.",
    detail:
      "Its claim is that the desk's fixed σ₁₅ is the wrong ruler during a fast tape, and that recomputing fair value " +
      "with live volatility finds mispricings the standard model misses.\n\n" +
      "It is usually dormant, for the same reason the gap rule is: it is an edge rule, so it must beat the ask by the " +
      "fee plus 5¢, and a corrected probability under the standard shrink cannot get far enough from the market to do " +
      "that. Dormant is the correct behaviour, not a fault.",
    seeAlso: ["sigma", "minedge", "decision"],
  },
  {
    id: "timemachine", group: "record", term: "The Time Machine",
    short: "Replays every settled window, scans for patterns in wins and losses, and judges each on windows it never saw.",
    detail:
      "The user's ask, 2026-09-16: find the pattern in the wins and losses and see whether a structure can beat it. " +
      "The machine tries about forty patterns — what the market said at each minute, the gap in σ, the hour, the " +
      "last windows' results, reversals inside a window — and for each asks the tradable question: when this holds, " +
      "does the side happen more often than the market's price says?\n\n" +
      "It is built so that it cannot fool you, because scanning forty patterns on one record WILL produce two or " +
      "three that look like winners by chance. Four rules. The windows are split by time and the newer 30% is never " +
      "looked at when a pattern picks its side. The side is chosen on the older windows only. The held-out edge is " +
      "reported with its interval — and that interval is widened for all forty tries, so the bar for REAL is about " +
      "3σ, not 1.96σ. And a survivor is replayed as trades on the held-out windows at the maker and taker prices, " +
      "fees included, so the verdict is money.\n\n" +
      "Rows marked “would pass uncorrected” are the ones to be most suspicious of, not least: they are precisely " +
      "what a scan invents from noise. The machine was tested on a market where outcomes are drawn at the market's " +
      "own price — pure noise — and it must find nothing there, across many seeds, before it is trusted with the " +
      "real record.",
    seeAlso: ["edge", "windows", "brier", "maker"],
  },
  {
    id: "edge", group: "decision", term: "Why there is no edge to find",
    short: "Twelve signals were tested against the market price. Not one beat it.",
    detail:
      "The desk was asked to win by predicting better. It was measured instead, and the measurement says prediction is " +
      "not the available lever.\n\n" +
      "Twelve candidate signals — the gap, momentum over five and ten minutes, acceleration, move since the open, order- " +
      "book imbalance, spread, market drift, how lopsided the book is, time of day, and two interactions — were each " +
      "fitted against the market price and scored on windows they were not trained on. Every one scored WORSE than " +
      "simply believing the market. The desk's own gap model was the worst single signal, and all twelve together were " +
      "worse still.\n\n" +
      "One appeared to work at minute 5. Across four different training cut-offs it scored −0.0029, −0.0041, +0.0224, " +
      "−0.0033 — it “works” at exactly one split, which is what noise looks like.\n\n" +
      "The one non-chart idea, that Coinbase might lead the Kalshi book, was measured directly: +0.67¢ per σ, with a " +
      "95% interval of −1.38¢ to +2.72¢. The estimate clears the spread; the interval says it is indistinguishable from " +
      "nothing at all.\n\n" +
      "So the desk stopped trying to forecast better and started refusing expensive trades. With no edge, the expected " +
      "result of a wager is exactly minus the fee — which makes the fee the only thing left worth managing.",
    seeAlso: ["fee", "floor", "retired", "holdtest"],
  },
];

// ── where each term lives on the tab, and which live indicator shows it ───
// Kept as tables rather than fields on each entry so the prose above stays
// readable, and so a layout change is one edit in one place.
export const SPOT_BY_ID = {
  target: "hero.target", now: "hero.now", gap: "hero.now", tps: "hero.now",
  knownsofar: "decided", settlement: "chart",
  price: "market.up", maker: "market.fee", sizing: "agents.grid", mid: "market.mid", spread: "market.mid", fee: "market.fee",
  allin: "market.allin", multiple: "market.mult", contracts: "market.up",
  payout: "market.up", book: "sheet.window",
  raw: "sheet.agent", agentcard: "agents.grid", calibrated: "agents.prob", fairvalue: "indicators", z: "indicators", sigma: "indicators",
  decision: "agents.grid", agrees: "agents.grid", floor: "indicators", minedge: "rules",
  shots: "rules", bankfloor: "sheet.agent", retired: "sheet.agent",
  "echo-rule": "rules", "kestrel-rule": "rules", edge: "indicators",
  mark: "agents.grid", grossnet: "agents.grid", take: "rules", stop: "rules", trail: "rules", holdtest: "rules", closereason: "sheet.agent",
  pnl: "agents.grid", ror: "agents.judge", winrate: "agents.judge", brier: "sheet.agent",
  curve: "sheet.agent", holdout: "sheet.agent", judgement: "agents.judge",
  windows: "rules", eras: "rules", projection: "rules", timemachine: "header",
  stake: "market.up", bank: "sheet.agent", graduation: "sheet.agent", currency: "header",
};

// The four live readings on the tab. An entry named here shows the REAL
// widget, not a drawing of one.
export const INDICATOR_BY_ID = {
  fee: "cost", floor: "cost", allin: "cost", spread: "cost",
  z: "odds", fairvalue: "odds", sigma: "odds", gap: "odds",
  knownsofar: "decided", settlement: "decided",
  payout: "payoff", contracts: "payoff", multiple: "payoff", stake: "payoff",
};

export const spotFor = (id) => SPOT_BY_ID[id] || null;
export const indicatorFor = (id) => INDICATOR_BY_ID[id] || null;

export const BY_ID = ENTRIES.reduce((m, e) => { m[e.id] = e; return m; }, {});
export const GROUP_LABEL = GROUPS.reduce((m, [k, v]) => { m[k] = v; return m; }, {});

/** Entries in a group, in declaration order. */
export const inGroup = (g) => ENTRIES.filter((e) => e.group === g);

/** Case-insensitive search over term, snippet and detail. */
export function searchGlossary(q) {
  const s = String(q || "").trim().toLowerCase();
  if (!s) return ENTRIES;
  return ENTRIES.filter((e) =>
    e.term.toLowerCase().includes(s) || e.short.toLowerCase().includes(s) || e.detail.toLowerCase().includes(s));
}

/** The worked example for an entry against live context, or null. */
export function workedFor(entry, ctx) {
  if (!entry?.worked || !ctx) return null;
  try {
    return entry.worked(ctx) || null;
  } catch {
    return null;                 // a half-loaded window must never break the sheet
  }
}
